import { useState } from "react";
import { Link } from "@tanstack/react-router";

import {
  useOpenQuotesCadence,
  type CadenceHealth,
  type CadenceTouchStatus,
  type ConversionMetric,
  type OpenQuoteBucket,
  type OpenQuoteCadenceItem,
  type OpenQuoteCadenceRail,
  type OpenQuoteCadenceTouch,
} from "../queries/useOpenQuotesCadence";
import { GhostMark } from "./GhostMark";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

const WINDOW_DAYS = 30;
const PAGE_SIZE = 25;

const STATUS_LABELS: Record<CadenceTouchStatus, string> = {
  sent: "Sent",
  delivered: "Delivered",
  queued: "Queued",
  preview: "Preview recorded",
  failed: "Failed",
  upcoming: "Upcoming",
  missed: "Missed",
  not_evaluated: "Not evaluated",
  suppressed: "Suppressed",
  eligibility_unknown: "Needs eligibility check",
  awaiting_previous_touch: "Awaiting previous touch",
};

const BUCKET_LABELS: Record<OpenQuoteBucket, string> = {
  standard_cadence: "Quote created",
  thinking: "Thinking",
  approved_pending_schedule: "Approved, pending schedule",
};

const REASON_LABELS: Record<string, string> = {
  awaiting_previous_touch: "Waiting for the previous touch",
  awaiting_daily_runtime_window: "Waiting for the next legal daily run",
  cross_channel_24h_gap: "Waiting for the 24-hour channel gap",
  cross_channel_opt_out: "Customer opted out across channels",
  customer_reply: "Customer replied",
  customer_reply_channel_blocked: "Customer reply pauses this channel",
  email_opt_out_signal: "Email contains an opt-out request",
  email_terminal_action: "Email classifier marked the quote lost",
  email_terminal_category: "Email has a terminal reply category",
  email_terminal_reason: "Email has a terminal reason",
  disposition_closed: "Closed by disposition",
  disposition_converted: "Converted by disposition",
  disposition_handled_until_new_inbound: "Handled until a new customer reply",
  draft_not_sent: "Preview recorded, not sent",
  due_pending_reminder: "A due reminder is still pending",
  first_touch_campaign_terminal: "First-touch campaign completed",
  future_reminder: "Future reminder set",
  j1_business_window_not_replayed: "J+1 eligibility needs a runtime check",
  j1_minimum_gap_not_reached: "Waiting for the J+1 minimum gap",
  j1_requires_completed_t3h: "J+1 needs a completed T+3h touch",
  latest_live_outbound_not_t3h: "A later live touch superseded T+3h",
  legacy_cutover_not_replayed: "Legacy eligibility needs a runtime check",
  legacy_conversation_handled: "Handled in the legacy conversation log",
  manual_shadow_audit_only: "Manual audit only",
  missing_email: "No email address",
  missing_phone: "No phone number",
  next_eligible_touch_without_delivery: "Eligible touch has no delivery record",
  not_due: "Not due yet",
  same_channel_sequence_gap: "Waiting for the same-channel sequence gap",
  shadow_preview_not_recorded: "No shadow preview recorded",
  t3h_overdue_schedule_requires_runtime_audit: "T+3h schedule needs a runtime check",
  t3h_runtime_eligibility_not_replayed: "T+3h eligibility needs a runtime check",
  t3h_scheduled: "T+3h scheduled",
  t3h_skipped: "T+3h suppressed",
  t3h_tracking_record_missing: "T+3h tracking record missing",
  v2_target_not_active: "V2 target is not active",
};

export function cadenceStatusLabel(status: CadenceTouchStatus) {
  return STATUS_LABELS[status];
}

export function quoteBucketLabel(bucket: OpenQuoteBucket) {
  return BUCKET_LABELS[bucket];
}

export function shadowTouchNote(key: string) {
  if (key === "j15_sms") return "Manual audit only";
  if (key === "j21_email") return "Preview recorded when available";
  return null;
}

export function OpenQuotesCadenceInventory() {
  const [offset, setOffset] = useState(0);
  const inventory = useOpenQuotesCadence({
    windowDays: WINDOW_DAYS,
    limit: PAGE_SIZE,
    offset,
  });
  const data = inventory.data;

  return (
    <section
      data-testid="open-quotes-cadence"
      className="overflow-hidden border-y border-slate-200 bg-white"
    >
      <div className="px-4 py-5 sm:px-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold tracking-normal text-slate-950">
              Open quotes, last 30 days
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Created in the window, grouped by CRM lifecycle and observed cadence evidence.
            </p>
          </div>
          {data ? (
            <span className="text-xs text-slate-500">
              Updated {formatTimestamp(data.generated_at, data.business_time_zone)}
            </span>
          ) : null}
        </div>

        {inventory.isLoading && !data ? <LoadingState /> : null}
        {inventory.isError && !data ? <ErrorState /> : null}
        {data ? (
          <>
            <MetricsStrip
              openQuotes={data.metrics.open_quotes}
              openValue={data.metrics.open_value_usd}
              mature7d={data.metrics.mature_7d}
              mature14d={data.metrics.mature_14d}
            />
            {data.items.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="mt-5 divide-y divide-slate-200 border-y border-slate-200">
                {data.items.map((item) => (
                  <QuoteCadenceRow
                    key={item.quote_id}
                    item={item}
                    businessTimeZone={data.business_time_zone}
                  />
                ))}
              </div>
            )}
            <Pagination
              offset={data.pagination.offset}
              limit={data.pagination.limit}
              total={data.pagination.total}
              hasMore={data.pagination.has_more}
              busy={inventory.isFetching}
              onPrevious={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}
              onNext={() => setOffset((current) => current + PAGE_SIZE)}
            />
          </>
        ) : null}
      </div>
    </section>
  );
}

function LoadingState() {
  return (
    <div
      aria-live="polite"
      className="mt-5 grid min-h-32 place-items-center border-y border-slate-100 bg-slate-50 text-sm text-slate-600"
    >
      Loading 30-day quote inventory...
    </div>
  );
}

function ErrorState() {
  return (
    <div
      role="alert"
      className="mt-5 border-y border-red-200 bg-red-50 px-4 py-6 text-sm text-red-800"
    >
      30-day quote inventory could not load. Existing quote tools remain unchanged.
    </div>
  );
}

function EmptyState() {
  return (
    <div className="mt-5 border-y border-emerald-200 bg-emerald-50 px-4 py-8 text-center text-sm text-emerald-800">
      No open quotes created in the last 30 days.
    </div>
  );
}

function MetricsStrip({
  openQuotes,
  openValue,
  mature7d,
  mature14d,
}: {
  openQuotes: number;
  openValue: number;
  mature7d: ConversionMetric;
  mature14d: ConversionMetric;
}) {
  return (
    <dl className="mt-5 grid grid-cols-2 border-y border-slate-200 sm:grid-cols-4">
      <Metric label="Open quotes" value={String(openQuotes)} />
      <Metric label="Open value" value={formatUsd(openValue)} />
      <Metric label="Quotes aged 7d+ converted" value={formatConversion(mature7d)} />
      <Metric label="Quotes aged 14d+ converted" value={formatConversion(mature14d)} />
    </dl>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 border-b border-slate-100 px-3 py-4 even:border-l sm:border-b-0 sm:border-l sm:first:border-l-0">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-1 break-words text-lg font-semibold text-slate-950">{value}</dd>
    </div>
  );
}

function QuoteCadenceRow({
  item,
  businessTimeZone,
}: {
  item: OpenQuoteCadenceItem;
  businessTimeZone: string;
}) {
  return (
    <article className="py-5 first:pt-4 last:pb-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/quotes/$quoteId"
              params={{ quoteId: item.quote_id }}
              className="font-semibold text-blue-700 hover:text-blue-900"
            >
              Quote #{item.quote_id}
            </Link>
            <BucketBadge bucket={item.bucket} />
            <HealthBadge health={item.cadence_health} />
          </div>
          <p className="mt-1 truncate text-sm font-medium text-slate-900">{item.customer_name}<GhostMark ghosted={item.ghosted} /></p>
          <p className="mt-1 text-xs text-slate-500">
            {formatUsd(item.total_usd)} | {item.age_days}d old | created{" "}
            {formatCadenceDateOnly(item.created_on.slice(0, 10))}
          </p>
        </div>
        <QuoteEvidence item={item} businessTimeZone={businessTimeZone} />
      </div>

      <div className="mt-4 grid gap-3 xl:grid-cols-3">
        {item.rails.map((rail) => (
          <CadenceRail
            key={rail.id}
            quoteId={item.quote_id}
            rail={rail}
            businessTimeZone={businessTimeZone}
          />
        ))}
      </div>
    </article>
  );
}

function QuoteEvidence({
  item,
  businessTimeZone,
}: {
  item: OpenQuoteCadenceItem;
  businessTimeZone: string;
}) {
  return (
    <dl className="grid min-w-0 grid-cols-1 gap-x-5 gap-y-1 text-xs sm:grid-cols-2 lg:max-w-[760px] lg:grid-cols-4">
      <div className="min-w-0">
        <dt className="text-slate-500">Last inbound</dt>
        <dd className="truncate font-medium text-slate-800">
          {contactEvidence(
            item.last_inbound_at,
            item.last_inbound_channel,
            "inbound",
            businessTimeZone,
          )}
        </dd>
      </div>
      <div className="min-w-0">
        <dt className="text-slate-500">Last outbound</dt>
        <dd className="truncate font-medium text-slate-800">
          {contactEvidence(
            item.last_outbound_at,
            item.last_outbound_channel,
            "outbound",
            businessTimeZone,
          )}
        </dd>
      </div>
      <div className="min-w-0">
        <dt className="text-slate-500">Disposition</dt>
        <dd className="truncate font-medium text-slate-800">
          {item.disposition?.state ?? "None"}
        </dd>
      </div>
      <div className="min-w-0">
        <dt className="text-slate-500">Suppression</dt>
        <dd className="truncate font-medium text-slate-800">
          {item.suppression.active
            ? humanizeReason(item.suppression.reason)
            : "None"}
        </dd>
      </div>
    </dl>
  );
}

function CadenceRail({
  quoteId,
  rail,
  businessTimeZone,
}: {
  quoteId: string;
  rail: OpenQuoteCadenceRail;
  businessTimeZone: string;
}) {
  const railClass = rail.id === "v1_live"
    ? "border-blue-200 bg-blue-50/50"
    : rail.id === "v1_shadow"
      ? "border-amber-200 bg-amber-50/50"
      : "border-slate-300 bg-slate-50";
  const railState = rail.id === "v1_live"
    ? "Live cadence evidence"
    : rail.id === "v1_shadow"
      ? "Preview evidence only"
      : "Not active";

  return (
    <section
      data-testid={`cadence-rail-${quoteId}-${rail.id}`}
      className={`min-w-0 border-l-4 px-3 py-3 ${railClass}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-semibold tracking-normal text-slate-950">{rail.label}</h3>
        <span className="text-[11px] text-slate-500">{railState}</span>
      </div>
      {rail.touches.length === 0 ? (
        <p className="mt-3 text-xs text-slate-500">No touch evidence in this rail.</p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {rail.touches.map((touch) => (
            <CadenceTouch
              key={touch.key}
              touch={touch}
              rail={rail}
              businessTimeZone={businessTimeZone}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function CadenceTouch({
  touch,
  rail,
  businessTimeZone,
}: {
  touch: OpenQuoteCadenceTouch;
  rail: OpenQuoteCadenceRail;
  businessTimeZone: string;
}) {
  const status = rail.id === "v2_target" ? "not_evaluated" : touch.status;
  const note = rail.id === "v1_shadow" ? shadowTouchNote(touch.key) : null;
  const evidenceDate = touch.actual_at
    ? `Recorded ${formatCadenceInstant(touch.actual_at, businessTimeZone)}`
    : rail.id !== "v2_target" && touch.due_at
      ? `Due ${formatCadenceInstant(touch.due_at, businessTimeZone)}`
      : rail.id !== "v2_target" && touch.due_on
        ? `Due ${formatCadenceDateOnly(touch.due_on)}`
        : null;
  return (
    <div
      className="min-w-[116px] flex-1 border border-white/80 bg-white px-2.5 py-2 shadow-sm"
      title={touch.reason ? humanizeReason(touch.reason) : undefined}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-slate-900">{touch.label}</span>
        <span className="text-[10px] font-medium uppercase text-slate-500">{touch.channel}</span>
      </div>
      <div className="mt-1.5">
        <StatusBadge status={status} />
      </div>
      {evidenceDate ? <p className="mt-1 text-[10px] text-slate-600">{evidenceDate}</p> : null}
      {note ? <p className="mt-1 break-all text-[10px] text-slate-500">{note}</p> : null}
      {touch.error ? <p className="mt-1 truncate text-[10px] text-red-700">{touch.error}</p> : null}
    </div>
  );
}

function StatusBadge({ status }: { status: CadenceTouchStatus }) {
  const variant = status === "sent" || status === "delivered"
    ? "green"
    : status === "failed" || status === "missed"
      ? "red"
      : status === "upcoming" || status === "queued"
        ? "amber"
        : status === "preview"
          ? "default"
          : "slate";
  return <Badge variant={variant}>{cadenceStatusLabel(status)}</Badge>;
}

function BucketBadge({ bucket }: { bucket: OpenQuoteBucket }) {
  const variant = bucket === "standard_cadence"
    ? "default"
    : bucket === "thinking"
      ? "amber"
      : "green";
  return <Badge variant={variant}>{quoteBucketLabel(bucket)}</Badge>;
}

function HealthBadge({ health }: { health: CadenceHealth }) {
  const labels: Record<CadenceHealth, string> = {
    on_track: "On track",
    needs_audit: "Needs audit",
    suppressed: "Suppressed",
    not_evaluated: "Not evaluated",
    eligibility_unknown: "Needs eligibility check",
  };
  const variant = health === "on_track"
    ? "green"
    : health === "needs_audit"
      ? "red"
      : "slate";
  return <Badge variant={variant}>{labels[health]}</Badge>;
}

function Pagination({
  offset,
  limit,
  total,
  hasMore,
  busy,
  onPrevious,
  onNext,
}: {
  offset: number;
  limit: number;
  total: number;
  hasMore: boolean;
  busy: boolean;
  onPrevious: () => void;
  onNext: () => void;
}) {
  if (total === 0) return null;
  const start = offset + 1;
  const end = Math.min(offset + limit, total);
  return (
    <nav
      aria-label="Open quote pages"
      className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <span className="text-xs text-slate-500">
        Showing {start}-{end} of {total}
      </span>
      <div className="flex gap-2">
        <Button variant="secondary" onClick={onPrevious} disabled={busy || offset === 0}>
          Previous
        </Button>
        <Button variant="secondary" onClick={onNext} disabled={busy || !hasMore}>
          Next
        </Button>
      </div>
    </nav>
  );
}

function formatUsd(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatConversion(metric: ConversionMetric) {
  const rate = metric.rate === null ? "n/a" : `${(metric.rate * 100).toFixed(1)}%`;
  return `${metric.converted} / ${metric.denominator} (${rate})`;
}

export function formatCadenceDateOnly(value: string) {
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
  if (!dateOnly) return value;
  const date = new Date(`${dateOnly}T12:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function formatCadenceInstant(value: string, timeZone: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone,
  }).format(date);
}

function formatTimestamp(value: string, timeZone: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  }).format(date);
}

function contactEvidence(
  at: string | null,
  channel: "sms" | "email" | null,
  direction: "inbound" | "outbound",
  timeZone: string,
) {
  if (!at) return "None recorded";
  return `${direction} ${channel ?? "contact"} | ${formatCadenceInstant(at, timeZone)}`;
}

function humanizeReason(reason: string | null) {
  if (!reason) return "Active";
  if (REASON_LABELS[reason]) return REASON_LABELS[reason];
  if (reason.endsWith("_recorded")) {
    const status = reason
      .slice(0, -"_recorded".length)
      .replace(/_/g, " ");
    return `${status.charAt(0).toUpperCase()}${status.slice(1)} recorded`;
  }
  return "Operational status recorded";
}
